import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Shield } from 'lucide-react';

const TermsPage = () => {
  const sections = [
    {
      num: "01",
      title: "Acceptance of Terms",
      content: [
        "By engaging Snigdha360 for any service — whether through our website, a signed engagement letter, verbal agreement, or commencement of work — you (\"Client\") agree to be bound by these Terms of Service (\"Terms\"). These Terms constitute a legally binding agreement between you and Snigdha360.",
        "If you are engaging on behalf of a business entity, you represent that you have the authority to bind that entity to these Terms. If you do not agree with any part of these Terms, you must not use our services."
      ],
      callout: "Note: These Terms apply to all clients globally, including those engaging Snigdha360 for services governed by United States or Indian regulatory frameworks. Jurisdiction-specific provisions are noted where applicable."
    },
    {
      num: "02",
      title: "Services Provided",
      content: [
        "Snigdha360 provides professional compliance, formation, and tax services across the following practice areas. The specific scope of services for each engagement will be documented in a separate engagement letter or service agreement provided to you prior to commencement of work."
      ],
      list: [
        "Business entity formation and registration (United States and India)",
        "Employer Identification Number (EIN) procurement with the IRS",
        "Federal, state, and local tax preparation and filing (United States)",
        "Income tax, GST, and related filings (India)",
        "State-level business registrations, amendments, and annual compliance",
        "Regulatory advisory and compliance monitoring",
        "Secure document management and client data governance",
        "Due diligence preparation and investment-readiness advisory"
      ],
      footer: "Snigdha360 does not provide legal advice and is not a law firm. Clients requiring legal counsel are advised to retain qualified legal professionals independently."
    },
    {
      num: "03",
      title: "Business Formation & EIN Services",
      jurisdictions: ["United States", "India"],
      content: [
        "Snigdha360 assists clients in the formation of business entities including but not limited to corporations (C-Corp, S-Corp), limited liability companies (LLCs), partnerships, and sole proprietorships in applicable US states, and Private Limited Companies, Limited Liability Partnerships (LLPs), and other structures under Indian company law.",
        "EIN Procurement (US): We facilitate the application for Employer Identification Numbers with the Internal Revenue Service (IRS) on behalf of our clients. EIN issuance is subject to IRS processing times and policies, which are outside our control.",
        "Charter Documents: All formation documents, articles of incorporation, operating agreements, and charter content prepared by Snigdha360 are drafted with future investment due diligence requirements in mind."
      ],
      list: [
        "Clients must provide accurate and complete information; Snigdha360 is not liable for errors arising from incorrect client-provided data",
        "Government filing fees are separate from Snigdha360 service fees and are payable by the client",
        "Processing timelines are estimates only and subject to government authority schedules",
        "Snigdha360 will retain copies of filed documents per our data retention policy"
      ]
    },
    {
      num: "04",
      title: "Tax Filing Services",
      jurisdictions: ["United States", "India"],
      content: [
        "United States: Snigdha360 is an IRS-authorized e-file provider. Federal and state tax returns are prepared and submitted in accordance with IRS requirements and applicable state tax authority rules.",
        "India: Tax preparation and filing services for Indian clients are conducted in accordance with the Income Tax Act, 1961, GST legislation, and all applicable central and state regulations in force at the time of filing."
      ],
      callout: "Client Responsibility: The accuracy of any tax return is contingent on the completeness and accuracy of information provided by the client. Snigdha360 relies on client-provided documentation and representations.",
      list: [
        "Tax advice provided is based on current law and may be affected by future legislative or regulatory changes",
        "Snigdha360 does not guarantee tax outcomes, refunds, or specific tax positions",
        "Extension filings, amended returns, and responses to tax authority notices may require separate engagement",
        "Penalties or interest arising from late filing due to client delays are the responsibility of the client"
      ]
    },
    {
      num: "05",
      title: "State & Regulatory Filings",
      jurisdictions: ["United States", "India"],
      content: [
        "Snigdha360 manages ongoing state-level compliance obligations including annual reports, registered agent maintenance, business license renewals, and regulatory amendments across applicable US states. In India, we manage Registrar of Companies (RoC) filings, GST registrations, and related statutory compliance requirements.",
        "We monitor regulatory changes as part of our practice and advise clients on material changes that may affect their compliance obligations. However, clients retain ultimate responsibility for ensuring their businesses comply with all applicable laws."
      ],
      list: [
        "Regulatory filing deadlines communicated by Snigdha360 are based on publicly available schedules",
        "Amendments to formation documents require client authorization before submission",
        "Snigdha360 will provide advance notice of upcoming renewal or compliance deadlines",
        "State and regulatory filing fees are payable by the client and are separate from service fees"
      ]
    },
    {
      num: "06",
      title: "Data & Document Management",
      content: [
        "Snigdha360 operates as an ISO 27001-aligned organization. Our data management practices reflect our commitment to protecting client information as a fundamental operating principle.",
        "Platform Security: All client documents are stored on our secure platform. Our system architecture is designed to prevent unauthorized downloading or extraction of client files. Where file access is operationally necessary, files must be removed from local systems within 7 days.",
        "Document Retention: We retain client documents for the period required by applicable law in each jurisdiction, after which documents are securely destroyed."
      ],
      list: [
        "Client data will not be sold, rented, or shared with third parties except as required by law",
        "All employees with access to client data operate under confidentiality agreements",
        "In the event of a data breach, Snigdha360 will notify affected clients in accordance with applicable laws",
        "Upon termination of engagement, client data will be returned or destroyed as per written instruction"
      ],
      callout: "Our data governance practices are documented, auditable, and consistently applied across all engagements."
    },
    {
      num: "07",
      title: "Client Responsibilities",
      content: [
        "A successful engagement depends on accurate, timely, and complete information from the client. By engaging Snigdha360, you agree to:"
      ],
      list: [
        "Provide accurate, complete, and timely information and documentation as requested",
        "Notify Snigdha360 promptly of any changes in your business structure, ownership, or regulatory circumstances",
        "Review all deliverables, returns, and documents before approval or submission",
        "Pay fees as agreed in the applicable engagement letter or service agreement",
        "Not use Snigdha360's services for any unlawful purpose or in furtherance of any fraudulent activity"
      ],
      footer: "Snigdha360 reserves the right to suspend or terminate services if a client provides false, misleading, or incomplete information."
    },
    {
      num: "08",
      title: "Fees & Payment",
      content: [
        "Service fees are communicated in writing prior to the commencement of each engagement. Fees are based on the scope of services agreed upon and do not include applicable government filing fees, statutory charges, or third-party costs."
      ],
      list: [
        "Fees are due as specified in the engagement letter; late payments may attract a service charge",
        "Scope changes during an engagement may result in revised fees, communicated in writing",
        "Snigdha360 reserves the right to withhold delivery of final work product until outstanding fees are paid",
        "Refund requests will be considered on a case-by-case basis; fees for work completed are generally non-refundable",
        "All fees are exclusive of applicable taxes (GST in India, applicable sales tax in the US)"
      ]
    },
    {
      num: "09",
      title: "Confidentiality",
      content: [
        "Snigdha360 treats all client information as strictly confidential. We will not disclose client information to any third party except: (a) as required by applicable law or regulatory authority; (b) with the client's express written consent; or (c) as necessary to deliver the agreed services through vetted sub-processors.",
        "This confidentiality obligation survives the termination of the engagement and remains in effect indefinitely with respect to information that does not enter the public domain through other lawful means."
      ]
    },
    {
      num: "10",
      title: "Limitation of Liability",
      content: [
        "To the maximum extent permitted by applicable law, Snigdha360's total liability to any client arising from or related to any engagement shall not exceed the total fees paid by that client in the twelve (12) months preceding the event giving rise to the claim.",
        "Snigdha360 shall not be liable for any indirect, incidental, consequential, or punitive damages, including lost profits, loss of business opportunity, or regulatory penalties arising from client-provided errors, government processing delays, or changes in law."
      ],
      callout: "Important: Tax laws and regulatory requirements change. Advice provided is based on the law in effect at the time of the engagement. Snigdha360 is not liable for impacts arising from legislative changes occurring after advice is delivered."
    },
    {
      num: "11",
      title: "Dispute Resolution",
      content: [
        "In the event of a dispute arising from or relating to these Terms or any engagement with Snigdha360, the parties agree to first attempt resolution through good-faith negotiation. If negotiation does not resolve the dispute within 30 days, either party may escalate the matter to mediation.",
        "Snigdha360 maintains a formal client complaint and feedback process. All complaints are acknowledged within 3 business days and addressed according to our documented resolution process."
      ]
    },
    {
      num: "12",
      title: "Governing Law",
      content: [
        "For clients engaging Snigdha360 for services primarily governed by United States law, these Terms shall be interpreted under and governed by the laws of the applicable US state of engagement.",
        "For clients engaging Snigdha360 for services primarily governed by Indian law, these Terms shall be governed by the laws of India, and disputes shall be subject to the jurisdiction of competent courts.",
        "For engagements spanning both jurisdictions, the governing law applicable to the primary service shall control, as specified in the engagement letter."
      ]
    },
    {
      num: "13",
      title: "Modifications to These Terms",
      content: [
        "Snigdha360 reserves the right to update these Terms at any time. Material changes will be communicated to active clients via email with a minimum of 14 days' notice before the revised Terms take effect.",
        "The most current version of these Terms will always be available at snigdha360.com/terms."
      ]
    },
    {
      num: "14",
      title: "Contact",
      content: [
        "Questions about these Terms, our services, or any aspect of your engagement may be directed to us at:"
      ],
      list: [
        "Email: legal@snigdha360.com",
        "Website: snigdha360.com"
      ],
      footer: "For data-related concerns or to exercise your rights under applicable privacy law, please refer to our Privacy Policy."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased">
      {/* Navigation - Same as Landing Page */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="Snigdha & Associates" 
                className="w-10 h-10 object-contain"
              />
              <div>
                <p className="text-sm font-medium text-slate-900">Snigdha & Associates</p>
                <p className="text-xs text-slate-500">Chartered Accountants</p>
              </div>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/#services" className="text-sm text-slate-600 hover:text-slate-900 transition-colors hidden md:block">India</Link>
              <Link to="/#us-services" className="text-sm text-slate-600 hover:text-slate-900 transition-colors hidden md:block">US</Link>
              <Link to="/#contact" className="text-sm text-slate-600 hover:text-slate-900 transition-colors hidden md:block">Contact</Link>
              <Link to="/login">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 h-auto">
                  Client Portal
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm text-emerald-600 uppercase tracking-widest mb-4 font-medium">Legal Documentation</p>
          <h1 className="text-4xl md:text-5xl font-light mb-6 text-slate-900">
            Terms of <span className="text-slate-400">Service</span>
          </h1>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
            <div>
              <p className="text-xs text-slate-400 uppercase mb-1">Effective Date</p>
              <p className="text-sm text-slate-900">January 1, 2025</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase mb-1">Last Updated</p>
              <p className="text-sm text-slate-900">January 1, 2025</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase mb-1">Jurisdictions</p>
              <p className="text-sm text-slate-900">United States & India</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase mb-1">Entity</p>
              <p className="text-sm text-slate-900">Snigdha360</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto space-y-16">
          {sections.map((section, i) => (
            <div key={i} className="relative">
              <div className="flex items-start gap-6">
                <span className="text-4xl font-light text-slate-200 flex-shrink-0 w-16">{section.num}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <h2 className="text-xl font-medium text-slate-900">{section.title}</h2>
                    {section.jurisdictions?.map((j, idx) => (
                      <span key={idx} className={`text-xs px-2 py-0.5 rounded ${
                        j === 'United States' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'
                      }`}>
                        {j === 'United States' ? 'US' : 'India'}
                      </span>
                    ))}
                  </div>
                  
                  {section.content?.map((para, idx) => (
                    <p key={idx} className="text-slate-600 leading-relaxed mb-4">{para}</p>
                  ))}
                  
                  {section.list && (
                    <ul className="space-y-2 my-4">
                      {section.list.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-slate-500 text-sm">
                          <span className="text-slate-300 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {section.callout && (
                    <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 my-4">
                      <p className="text-sm text-slate-600">{section.callout}</p>
                    </div>
                  )}
                  
                  {section.footer && (
                    <p className="text-slate-500 text-sm mt-4">{section.footer}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer - Same as Landing Page */}
      <footer className="border-t border-slate-200 py-12 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="Snigdha & Associates" 
                className="w-8 h-8 object-contain"
              />
              <div>
                <p className="text-sm text-slate-700">Snigdha & Associates</p>
                <p className="text-xs text-slate-400">Chartered Accountants</p>
              </div>
            </Link>
            
            <div className="flex items-center gap-6">
              <Link to="/terms" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">Terms</Link>
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">Privacy</Link>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-100">
                <Shield className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-xs text-emerald-700">ISO 27001</span>
              </div>
              <p className="text-xs text-slate-400">© 2026 All rights reserved</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
